import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle,
  Eye,
  FileDown,
  Loader2,
  Minus,
  Package,
  Plus,
  Printer,
  RotateCcw,
  Search,
  User,
} from 'lucide-react';
import apiService from '../../services/api';
import {
  buildRetailReturnSearchRequest,
  isCurrentSearchGeneration,
  isRetailReturnSearchResponseStillValid,
  resolveRetailReturnBackNavigation,
  retailReturnBarcodeChangeEffects,
  retailReturnQueryChangeEffects,
  retailReturnSearchHintMessage,
} from '../../utils/posRetailReturnSearch';
import { buildCaisseFideliteDisplay } from '../../utils/caisseTicketFideliteDisplay.js';
import { isFactureTelechargeable } from '../../utils/commandeFactureDisponible.js';
import CaisseDuplicataPreviewModal from './CaisseDuplicataPreviewModal.jsx';
import CaisseTicketConsultationModal from './CaisseTicketConsultationModal.jsx';
import CaisseCommandeFactureAvoirActions from './CaisseCommandeFactureAvoirActions.jsx';

function fmtMoney(value, devise = 'EUR') {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: devise }).format(n);
}

export default function PosRetailReturnPanel({
  commercantId,
  commercantProfile = null,
  devise = 'EUR',
  onBack,
  onSuccess,
  showToast,
  /** U2-S1a — ouverture directe ticket depuis fiche Retours unifiés */
  initialTicketId = null,
  prefilledFromUnified = false,
}) {
  const [step, setStep] = useState('search');
  const [query, setQuery] = useState('');
  const [barcode, setBarcode] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchStatus, setSearchStatus] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [qtyByLine, setQtyByLine] = useState({});
  const [restock, setRestock] = useState(true);
  const [methode, setMethode] = useState('ESPECES');
  const [motif, setMotif] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [ticketDetailCache, setTicketDetailCache] = useState(null);
  const [ticketDetailFidelite, setTicketDetailFidelite] = useState(null);
  const [consultOpen, setConsultOpen] = useState(false);
  const [consultLoading, setConsultLoading] = useState(false);
  const [duplicataPreview, setDuplicataPreview] = useState(null);
  const [factureLoading, setFactureLoading] = useState(false);
  const [avoirLoadingId, setAvoirLoadingId] = useState(null);
  const [docActionLoading, setDocActionLoading] = useState(false);

  const searchSeqRef = useRef(0);
  const initialTicketOpenedRef = useRef(false);

  const resetSelection = useCallback(() => {
    setQtyByLine({});
    setRestock(true);
    setMotif('');
  }, []);

  const runSearch = useCallback(
    async (trigger = 'manual') => {
      const preferBarcode = trigger === 'barcode_button';
      const built = buildRetailReturnSearchRequest(query, barcode, { preferBarcode });
      if (!built.ok) {
        if (built.reason === 'query_too_short' && (trigger === 'text' || trigger === 'barcode_button')) {
          showToast('Saisissez au moins 2 caractères ou scannez un code complet', 'error');
        }
        if (built.reason === 'barcode_incomplete') {
          setSearchStatus('typing_barcode');
          setTickets([]);
        }
        return;
      }

      const seq = ++searchSeqRef.current;
      const builtAtRequest = built;
      const preferBarcodeAtRequest = preferBarcode;
      setSearchLoading(true);
      setSearchStatus('loading');
      setTickets([]);

      try {
        const res = await apiService.searchRetailReturns(built.params, commercantId);
        if (!isCurrentSearchGeneration(seq, searchSeqRef.current)) return;
        if (
          !isRetailReturnSearchResponseStillValid(
            builtAtRequest,
            query,
            barcode,
            preferBarcodeAtRequest
          )
        ) {
          return;
        }

        setTickets(res.tickets || []);
        const hintMsg = retailReturnSearchHintMessage(res.hint);
        if (hintMsg) {
          showToast(hintMsg, 'error');
          setSearchStatus('empty');
        } else if ((res.tickets || []).length === 0) {
          setSearchStatus('empty');
        } else {
          setSearchStatus('results');
        }
      } catch (e) {
        if (!isCurrentSearchGeneration(seq, searchSeqRef.current)) return;
        setTickets([]);
        setSearchStatus('error');
        showToast(e.message || 'Recherche impossible', 'error');
      } finally {
        if (isCurrentSearchGeneration(seq, searchSeqRef.current)) {
          setSearchLoading(false);
        }
      }
    },
    [query, barcode, commercantId, showToast]
  );

  const handleBarcodeChange = (value) => {
    setBarcode(value);
    const effects = retailReturnBarcodeChangeEffects(value);
    if (effects.invalidateGeneration) {
      searchSeqRef.current += 1;
      setSearchLoading(false);
    }
    if (effects.clearTickets) setTickets([]);
    if (effects.searchStatus === 'typing_barcode') {
      setSearchStatus('typing_barcode');
    } else if (searchStatus !== 'loading') {
      setSearchStatus(effects.searchStatus);
    }
  };

  const goBackToSearchList = useCallback(() => {
    setStep('search');
    setPreview(null);
  }, []);

  const handlePanelBack = useCallback(() => {
    const action = resolveRetailReturnBackNavigation({
      step,
      consultOpen,
      duplicataOpen: !!duplicataPreview,
    });
    switch (action) {
      case 'close_consult':
        setConsultOpen(false);
        break;
      case 'close_duplicata':
        setDuplicataPreview(null);
        break;
      case 'back_to_list':
        if (prefilledFromUnified) {
          onBack?.();
        } else {
          goBackToSearchList();
        }
        break;
      default:
        onBack?.();
    }
  }, [step, consultOpen, duplicataPreview, goBackToSearchList, onBack, prefilledFromUnified]);

  const openTicket = useCallback(
    async (ticketId) => {
      setPreviewLoading(true);
      setStep('detail');
      resetSelection();
      try {
        const data = await apiService.getRetailReturnPreview(ticketId, commercantId);
        setPreview(data);
        const initial = {};
        (data.lignes || []).forEach((l) => {
          if (l.quantiteRetournable > 0) initial[l.ligneId] = 0;
        });
        setQtyByLine(initial);
        setRestock(Boolean(data.defaultRestockSuggested));
        setMethode(data.refundMethodsSuggested?.[0] || 'ESPECES');
      } catch (e) {
        showToast(e.message || 'Ticket indisponible', 'error');
        setStep('search');
      } finally {
        setPreviewLoading(false);
      }
    },
    [commercantId, resetSelection, showToast]
  );

  useEffect(() => {
    if (!initialTicketId || initialTicketOpenedRef.current) return;
    initialTicketOpenedRef.current = true;
    openTicket(initialTicketId);
  }, [initialTicketId, openTicket]);

  const loadTicketDetailForDocs = useCallback(
    async (ticketId) => {
      const data = await apiService.getTicketDetail(ticketId, commercantId);
      const ticket = data?.ticket || null;
      if (!ticket) {
        throw new Error('Ticket introuvable');
      }
      const fideliteDisplay =
        data?.fideliteDisplay ||
        buildCaisseFideliteDisplay({
          commande: ticket,
          rewardIdIntent: data?.rewardIdIntent || null,
          consommation: ticket.recompenseConsommations?.[0] || null,
        });
      setTicketDetailCache(ticket);
      setTicketDetailFidelite(fideliteDisplay);
      return { ticket, fideliteDisplay };
    },
    [commercantId]
  );

  const handleOpenConsultation = useCallback(async () => {
    if (!preview?.ticket?.id) return;
    setConsultOpen(true);
    if (ticketDetailCache?.id === preview.ticket.id) {
      setConsultLoading(false);
      return;
    }
    setConsultLoading(true);
    try {
      await loadTicketDetailForDocs(preview.ticket.id);
    } catch (e) {
      showToast(e.message || 'Impossible de charger le ticket', 'error');
      setConsultOpen(false);
    } finally {
      setConsultLoading(false);
    }
  }, [preview?.ticket?.id, ticketDetailCache?.id, loadTicketDetailForDocs, showToast]);

  const handleOpenDuplicata = useCallback(async () => {
    if (!preview?.ticket?.id || docActionLoading) return;
    setDocActionLoading(true);
    try {
      let ticket = ticketDetailCache;
      let display = ticketDetailFidelite;
      if (!ticket || ticket.id !== preview.ticket.id) {
        const loaded = await loadTicketDetailForDocs(preview.ticket.id);
        ticket = loaded.ticket;
        display = loaded.fideliteDisplay;
      }
      setDuplicataPreview({ ticket, display });
    } catch (e) {
      showToast(e.message || 'Duplicata indisponible', 'error');
    } finally {
      setDocActionLoading(false);
    }
  }, [
    preview?.ticket?.id,
    docActionLoading,
    ticketDetailCache,
    ticketDetailFidelite,
    loadTicketDetailForDocs,
    showToast,
  ]);

  const handleDownloadFacture = useCallback(async () => {
    if (!preview?.ticket?.id || factureLoading) return;
    setFactureLoading(true);
    try {
      let ticket = ticketDetailCache;
      if (!ticket || ticket.id !== preview.ticket.id) {
        const loaded = await loadTicketDetailForDocs(preview.ticket.id);
        ticket = loaded.ticket;
      }
      if (!isFactureTelechargeable(ticket)) {
        showToast('Aucune facture disponible pour ce ticket', 'info');
        return;
      }
      await apiService.downloadCommandeFacture(ticket.id);
      showToast('Facture téléchargée', 'success');
    } catch (e) {
      showToast(e.message || 'Facture indisponible', 'error');
    } finally {
      setFactureLoading(false);
    }
  }, [
    preview?.ticket?.id,
    factureLoading,
    ticketDetailCache,
    loadTicketDetailForDocs,
    showToast,
  ]);

  const factureAvailable =
    ticketDetailCache?.id === preview?.ticket?.id && isFactureTelechargeable(ticketDetailCache);

  const handleDownloadAvoir = useCallback(
    async (avoirId) => {
      if (!preview?.ticket?.id || !avoirId) return;
      setAvoirLoadingId(avoirId);
      try {
        await apiService.downloadCommandeAvoir(preview.ticket.id, avoirId);
        showToast('Avoir téléchargé', 'success');
      } catch (e) {
        showToast(e.message || 'Avoir indisponible', 'error');
      } finally {
        setAvoirLoadingId(null);
      }
    },
    [preview?.ticket?.id, showToast]
  );

  useEffect(() => {
    setTicketDetailCache(null);
    setTicketDetailFidelite(null);
    setConsultOpen(false);
    setDuplicataPreview(null);
  }, [preview?.ticket?.id]);

  useEffect(() => {
    if (!preview?.ticket?.id || ticketDetailCache?.id === preview.ticket.id) return;
    let cancelled = false;
    loadTicketDetailForDocs(preview.ticket.id).catch(() => {
      if (!cancelled) {
        /* silencieux — boutons afficheront erreur au clic */
      }
    });
    return () => {
      cancelled = true;
    };
  }, [preview?.ticket?.id, ticketDetailCache?.id, loadTicketDetailForDocs]);

  const selectedLines = (preview?.lignes || []).filter((l) => (qtyByLine[l.ligneId] || 0) > 0);
  const montantPreview = selectedLines.reduce(
    (s, l) => s + l.prixUnitaireTtc * (qtyByLine[l.ligneId] || 0),
    0
  );
  const solde = preview?.soldeRemboursable ?? 0;
  const canSubmit =
    selectedLines.length > 0 &&
    montantPreview > 0 &&
    montantPreview <= solde + 0.01 &&
    !submitLoading;

  const handleConfirm = async () => {
    if (!preview?.ticket?.id || !canSubmit) return;
    setSubmitLoading(true);
    try {
      const ligneIds = selectedLines.map((l) => l.ligneId);
      const ligneQuantites = Object.fromEntries(
        selectedLines.map((l) => [l.ligneId, qtyByLine[l.ligneId]])
      );
      const payload = {
        ligneIds,
        ligneQuantites,
        motif: motif || 'Retour retail caisse',
        methodePaiement: methode,
        restaurerStockPhysique: preview.stockManagementEnabled ? restock : false,
      };
      const result = await apiService.executeRetailReturn(preview.ticket.id, payload, commercantId);
      showToast(
        `Retour enregistré — ${fmtMoney(result.montantRembourse, devise)} remboursé`,
        'success'
      );
      if (result.avoirCreated && result.avoir?.numeroAvoir) {
        showToast(`Avoir ${result.avoir.numeroAvoir} émis`, 'info');
      }
      try {
        await loadTicketDetailForDocs(preview.ticket.id);
      } catch {
        /* détail docs rafraîchi au prochain clic */
      }
      onSuccess?.(result);
      setStep('search');
      setPreview(null);
      setTickets([]);
      setQuery('');
      setBarcode('');
      setSearchStatus(null);
    } catch (e) {
      showToast(e.message || 'Échec du retour', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const showEmptyHint =
    !searchLoading &&
    tickets.length === 0 &&
    searchStatus === 'empty';

  const showTypingBarcodeHint =
    !searchLoading && searchStatus === 'typing_barcode' && barcode.trim().length > 0;

  return (
    <div className="pos-caisse-work-surface" data-testid="caisse-retour-retail-view">
      <CaisseTicketConsultationModal
        open={consultOpen}
        loading={consultLoading}
        ticket={ticketDetailCache?.id === preview?.ticket?.id ? ticketDetailCache : null}
        fideliteDisplay={ticketDetailFidelite}
        formatMoney={(v) => fmtMoney(v, devise)}
        onClose={() => setConsultOpen(false)}
        factureAvailable={factureAvailable}
        factureLoading={factureLoading}
        onDownloadFacture={handleDownloadFacture}
        onDownloadAvoir={handleDownloadAvoir}
        avoirLoadingId={avoirLoadingId}
      />
      <CaisseDuplicataPreviewModal
        open={!!duplicataPreview}
        ticket={duplicataPreview?.ticket}
        display={duplicataPreview?.display}
        formatMoney={(v) => fmtMoney(v, devise)}
        commercant={commercantProfile}
        onClose={() => setDuplicataPreview(null)}
        backLabel="Retour au retour produits"
      />
      <header className="pos-caisse-work-surface-header flex flex-wrap items-start gap-3 mb-6">
        <button
          type="button"
          onClick={handlePanelBack}
          className="pos-touch-target p-2 rounded-xl hover:bg-slate-100 text-slate-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label={
            step === 'detail' || consultOpen || duplicataPreview
              ? 'Retour'
              : 'Retour accueil'
          }
          data-testid="retail-return-header-back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-violet-600 shrink-0" />
            Retour produits (retail)
          </h2>
          <p className="text-sm text-slate-500">Recherche ticket · sélection · remboursement</p>
        </div>
      </header>

      {step === 'search' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
            <label className="text-sm font-medium text-slate-700">N° ticket, client ou référence</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  const effects = retailReturnQueryChangeEffects();
                  if (effects.invalidateGeneration) {
                    searchSeqRef.current += 1;
                    setSearchLoading(false);
                  }
                  if (effects.clearBarcode) setBarcode('');
                  if (effects.clearTickets) setTickets([]);
                  if (searchStatus !== 'loading') {
                    setSearchStatus(effects.searchStatus);
                  }
                }}
                placeholder="Ex. T-2026… ou nom client"
                className="flex-1 border border-slate-200 rounded-xl px-4 py-3 text-sm min-h-[44px]"
                onKeyDown={(e) => e.key === 'Enter' && runSearch('text')}
              />
              <button
                type="button"
                onClick={() => runSearch('text')}
                disabled={searchLoading}
                className="px-4 py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700 disabled:opacity-50 flex items-center gap-2 min-h-[44px]"
              >
                {searchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Chercher
              </button>
            </div>
            <label className="text-sm font-medium text-slate-700">Scan CLKI / EAN (optionnel)</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={barcode}
                onChange={(e) => handleBarcodeChange(e.target.value)}
                placeholder="Code-barres produit complet"
                className="flex-1 border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono min-h-[44px]"
                data-testid="retail-return-barcode-input"
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    runSearch('barcode_button');
                  }
                }}
              />
              <button
                type="button"
                onClick={() => runSearch('barcode_button')}
                disabled={searchLoading || !barcode.trim()}
                className="px-4 py-3 bg-slate-100 text-slate-800 rounded-xl font-semibold hover:bg-slate-200 disabled:opacity-50 min-h-[44px]"
                aria-label="Rechercher par code"
              >
                Code
              </button>
            </div>
            {showTypingBarcodeHint && (
              <p className="text-xs text-slate-500" data-testid="retail-return-barcode-typing-hint">
                Saisie en cours — validez avec Entrée ou « Code » une fois le CLKI / EAN complet.
              </p>
            )}
          </div>

          <div className="space-y-2">
            {showEmptyHint && (
              <p className="text-center text-sm text-slate-500 py-8" data-testid="retail-return-no-tickets">
                Aucun ticket trouvé
              </p>
            )}
            {tickets.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => openTicket(t.id)}
                disabled={!t.hasReturnableQty}
                className="w-full text-left p-4 bg-white rounded-xl border border-slate-200 hover:border-violet-300 hover:shadow-md disabled:opacity-50 transition-all min-h-[44px]"
              >
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <p className="font-bold text-slate-900">{t.numeroTicket || t.numero || t.id.slice(-8)}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(t.dateCommande).toLocaleString('fr-FR')}
                      {t.clientLabel ? ` · ${t.clientLabel}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-800">{fmtMoney(t.total, devise)}</p>
                    <p className="text-xs text-violet-700">Solde {fmtMoney(t.soldeRemboursable, devise)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'detail' && (
        <div className="space-y-4">
          {previewLoading || !preview ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
            </div>
          ) : (
            <>
              <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 space-y-3">
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <p className="text-sm text-violet-800 font-medium">Ticket</p>
                    <p className="text-lg font-bold text-slate-900">
                      {preview.ticket.numeroTicket || preview.ticket.numero}
                    </p>
                    {preview.client && (
                      <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                        <User className="h-4 w-4" />
                        {[preview.client.prenom, preview.client.nom].filter(Boolean).join(' ') ||
                          preview.client.email}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-violet-700">Remboursable</p>
                    <p className="text-2xl font-bold text-violet-900">{fmtMoney(solde, devise)}</p>
                  </div>
                </div>
                <div
                  className="flex flex-wrap gap-2"
                  data-testid="retail-return-ticket-doc-actions"
                >
                  <button
                    type="button"
                    onClick={handleOpenConsultation}
                    disabled={docActionLoading}
                    className="flex-1 min-w-[140px] min-h-[48px] px-3 py-2.5 rounded-xl bg-white border border-violet-200 text-violet-900 text-sm font-semibold hover:bg-violet-100 flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="retail-return-view-ticket"
                  >
                    <Eye className="h-4 w-4 shrink-0" />
                    Voir le ticket
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenDuplicata}
                    disabled={docActionLoading}
                    className="flex-1 min-w-[140px] min-h-[48px] px-3 py-2.5 rounded-xl bg-white border border-violet-200 text-violet-900 text-sm font-semibold hover:bg-violet-100 flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="retail-return-duplicata"
                  >
                    {docActionLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                    ) : (
                      <Printer className="h-4 w-4 shrink-0" />
                    )}
                    Duplicata
                  </button>
                  {factureAvailable ? (
                    <button
                      type="button"
                      onClick={handleDownloadFacture}
                      disabled={factureLoading}
                      className="flex-1 min-w-[140px] min-h-[48px] px-3 py-2.5 rounded-xl bg-white border border-emerald-200 text-emerald-900 text-sm font-semibold hover:bg-emerald-50 flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="retail-return-facture"
                    >
                      {factureLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                      ) : (
                        <FileDown className="h-4 w-4 shrink-0" />
                      )}
                      Facture
                    </button>
                  ) : ticketDetailCache?.id === preview.ticket.id ? (
                    <p
                      className="w-full text-xs text-violet-700/80 px-1"
                      data-testid="retail-return-no-facture"
                    >
                      Aucune facture émise pour ce ticket.
                    </p>
                  ) : null}
                </div>
                {ticketDetailCache?.id === preview?.ticket?.id &&
                  ticketDetailCache.avoirs?.length > 0 && (
                    <CaisseCommandeFactureAvoirActions
                      commandeId={ticketDetailCache.id}
                      numeroFacture={ticketDetailCache.numeroFacture}
                      avoirs={ticketDetailCache.avoirs}
                      factureAvailable={false}
                      onDownloadAvoir={handleDownloadAvoir}
                      avoirLoadingId={avoirLoadingId}
                      formatMoney={(v) => fmtMoney(v, devise)}
                      testIdPrefix="retail-return"
                    />
                  )}
              </div>

              <div className="space-y-2">
                {(preview.lignes || []).map((l) => {
                  const qty = qtyByLine[l.ligneId] || 0;
                  const active = qty > 0;
                  const max = l.quantiteRetournable;
                  if (max <= 0) return null;
                  return (
                    <div
                      key={l.ligneId}
                      className={`rounded-xl border-2 p-4 ${active ? 'border-violet-400 bg-violet-50' : 'border-slate-200 bg-white'}`}
                    >
                      <div className="flex items-start gap-3">
                        <Package className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900">{l.nom}</p>
                          <p className="text-xs text-slate-500">
                            Vendu {l.quantiteVendue} · déjà retourné {l.quantiteDejaRetournee} · max {max}
                          </p>
                          <p className="text-sm text-slate-600">{fmtMoney(l.prixUnitaireTtc, devise)} / u</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setQtyByLine((prev) => ({
                                ...prev,
                                [l.ligneId]: Math.max(0, (prev[l.ligneId] || 0) - 1),
                              }))
                            }
                            className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-10 text-center font-bold">{qty}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setQtyByLine((prev) => ({
                                ...prev,
                                [l.ligneId]: Math.min(max, (prev[l.ligneId] || 0) + 1),
                              }))
                            }
                            className="w-8 h-8 rounded-lg bg-violet-200 flex items-center justify-center"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setQtyByLine((prev) => ({ ...prev, [l.ligneId]: max }))}
                            className="text-xs font-semibold text-violet-700 px-2"
                          >
                            Max
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {preview.stockManagementEnabled && preview.stockWasConsumed && (
                <label className="flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-xl cursor-pointer">
                  <input
                    type="checkbox"
                    checked={restock}
                    onChange={(e) => setRestock(e.target.checked)}
                    className="w-5 h-5 rounded border-slate-300 text-violet-600"
                  />
                  <span className="text-sm text-slate-700">Réintégrer les quantités au stock</span>
                </label>
              )}

              <div>
                <label className="text-sm font-medium text-slate-700">Mode de remboursement</label>
                <div className="flex gap-2 mt-2">
                  {(preview.refundMethodsSuggested || ['ESPECES', 'CARTE']).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMethode(m)}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 ${
                        methode === m
                          ? 'border-violet-500 bg-violet-50 text-violet-800'
                          : 'border-slate-200 text-slate-600'
                      }`}
                    >
                      {m === 'ESPECES' ? 'Espèces' : m === 'CARTE' ? 'Carte' : m}
                    </button>
                  ))}
                </div>
              </div>

              <input
                type="text"
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                placeholder="Motif (optionnel)"
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm"
              />

              <div className="sticky bottom-0 bg-white border-t border-slate-200 pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={goBackToSearchList}
                  className="flex-1 py-3 bg-slate-100 rounded-xl font-semibold text-slate-700"
                  data-testid="retail-return-back-to-list"
                >
                  Retour liste
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!canSubmit}
                  className="flex-1 py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="retail-return-confirm"
                >
                  {submitLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4" />
                  )}
                  Confirmer {montantPreview > 0 ? fmtMoney(montantPreview, devise) : ''}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
